import type { PoolPlacement, PoolApplication, PoolClimate, PoolUnit } from '@/api/calculators/types';
import { CLIMATE_OPTIONS, PLACEMENT_OPTIONS, APPLICATION_OPTIONS, HEAT_TIME_OPTIONS, TARGET_TEMP_OPTIONS } from './constants';
import { SELECT_CLASS, INPUT_CLASS, Field } from './PoolUi';

interface ForwardFlowProps {
  inputMode: 'dimensions' | 'volume';
  setInputMode: (value: 'dimensions' | 'volume') => void;
  volume: string;
  setVolume: (value: string) => void;
  volumeUnit: 'L' | 'm3';
  setVolumeUnit: (value: 'L' | 'm3') => void;
  climate: PoolClimate;
  setClimate: (v: PoolClimate) => void;
  unit: PoolUnit;
  setUnit: (v: PoolUnit) => void;
  length: number | '';
  setLength: (v: number | '') => void;
  width: number | '';
  setWidth: (v: number | '') => void;
  depthAvg: number | '';
  setDepthAvg: (v: number | '') => void;
  placement: PoolPlacement;
  setPlacement: (v: PoolPlacement) => void;
  application: PoolApplication;
  setApplication: (v: PoolApplication) => void;
  winterOp: 'yes' | 'no';
  setWinterOp: (v: 'yes' | 'no') => void;
  heatTimeTarget: 24 | 36 | 48;
  setHeatTimeTarget: (v: 24 | 36 | 48) => void;
  targetTemp: 26 | 27 | 28 | 29 | 30;
  setTargetTemp: (v: 26 | 27 | 28 | 29 | 30) => void;
  loading: boolean;
  onCalculate: () => void;
}

export function ForwardFlow(p: ForwardFlowProps) {
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm print:p-0 print:shadow-none">
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Pool size input">
        {(['dimensions', 'volume'] as const).map(mode => (
          <button key={mode} type="button" aria-pressed={p.inputMode === mode}
            onClick={() => p.setInputMode(mode)}
            className={`rounded-md border px-4 py-2 text-sm ${p.inputMode === mode ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}>
            {mode === 'dimensions' ? 'Enter by dimensions' : 'Enter by volume'}
          </button>
        ))}
      </div>
      {/* Climate + units row */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Climate zone">
          <select
            data-testid="pool-fwd-climate"
            value={p.climate}
            onChange={(e) => p.setClimate(e.target.value as PoolClimate)}
            className={SELECT_CLASS}
          >
            {CLIMATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        {p.inputMode === 'dimensions' && <Field label="Units">
          <select
            data-testid="pool-fwd-unit"
            value={p.unit}
            onChange={(e) => p.setUnit(e.target.value as PoolUnit)}
            className={SELECT_CLASS}
          >
            <option value="m">Meters</option>
            <option value="ft">Feet</option>
          </select>
        </Field>}
      </div>

      {p.inputMode === 'volume' ? <div className="mt-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pool volume">
            <input aria-label="Pool volume" data-testid="pool-fwd-volume" inputMode="decimal"
              value={p.volume} onChange={e => p.setVolume(e.target.value)} className={INPUT_CLASS}
              placeholder={p.volumeUnit === 'L' ? 'e.g. 65,000' : 'e.g. 25'} />
          </Field>
          <Field label="Volume unit">
            <select aria-label="Volume unit" data-testid="pool-fwd-volume-unit" value={p.volumeUnit}
              onChange={e => p.setVolumeUnit(e.target.value as 'L' | 'm3')} className={SELECT_CLASS}>
              <option value="m3">Cubic metres (m³)</option><option value="L">Litres (L)</option>
            </select>
          </Field>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-slate-600">
          Surface area estimated from typical depth ({p.application === 'residential' ? '1.35' : '1.5'} m). For exact sizing, use dimensions mode.
        </p>
      </div> : <>
      {/* Dimensions row */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <Field label="Length">
          <input
            type="number"
            step={0.1}
            data-testid="pool-fwd-length"
            value={p.length}
            onChange={(e) =>
              p.setLength(e.target.value === '' ? '' : Number(e.target.value))
            }
            className={INPUT_CLASS}
          />
        </Field>
        <Field label="Width">
          <input
            type="number"
            step={0.1}
            data-testid="pool-fwd-width"
            value={p.width}
            onChange={(e) =>
              p.setWidth(e.target.value === '' ? '' : Number(e.target.value))
            }
            className={INPUT_CLASS}
          />
        </Field>
        <Field label="Avg. depth">
          <input
            type="number"
            step={0.1}
            data-testid="pool-fwd-depth"
            value={p.depthAvg}
            onChange={(e) =>
              p.setDepthAvg(e.target.value === '' ? '' : Number(e.target.value))
            }
            className={INPUT_CLASS}
          />
        </Field>
      </div>
      </>}

      {/* Placement + Application */}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Placement">
          <select
            data-testid="pool-fwd-placement"
            value={p.placement}
            onChange={(e) => p.setPlacement(e.target.value as PoolPlacement)}
            className={SELECT_CLASS}
          >
            {PLACEMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Application">
          <select
            data-testid="pool-fwd-application"
            value={p.application}
            onChange={(e) =>
              p.setApplication(e.target.value as PoolApplication)
            }
            className={SELECT_CLASS}
          >
            {APPLICATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Winter / Heat-up / Target temp */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <Field label="Winter ops">
          <select
            data-testid="pool-fwd-winter"
            value={p.winterOp}
            onChange={(e) => p.setWinterOp(e.target.value as 'yes' | 'no')}
            className={SELECT_CLASS}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>
        <Field label="Heat-up target">
          <select
            data-testid="pool-fwd-heattime"
            value={p.heatTimeTarget}
            onChange={(e) =>
              p.setHeatTimeTarget(Number(e.target.value) as 24 | 36 | 48)
            }
            className={SELECT_CLASS}
          >
            {HEAT_TIME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Target temp">
          <select
            data-testid="pool-fwd-target"
            value={p.targetTemp}
            onChange={(e) =>
              p.setTargetTemp(Number(e.target.value) as 26 | 27 | 28 | 29 | 30)
            }
            className={SELECT_CLASS}
          >
            {TARGET_TEMP_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}°C
              </option>
            ))}
          </select>
        </Field>
      </div>

      <button
        type="button"
        onClick={p.onCalculate}
        disabled={p.loading}
        className="mt-4 w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400 print:hidden"
        data-testid="pool-fwd-calculate"
      >
        {p.loading ? 'Calculating…' : 'Calculate & Generate ROI'}
      </button>
    </div>
  );
}
